import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Shield, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/SEO/SEOHead';

export function ConfidentialitePage() {
  const { locale } = useParams<{ locale: string }>();
  const { t, i18n } = useTranslation('confidentialite');
  const currentLocale = locale || i18n.language || 'fr';

  const sections = t('sections', { returnObjects: true }) as any[];
  const alert = t('alert', { returnObjects: true }) as any;
  const companyInfo = t('companyInfo', { returnObjects: true }) as any;
  const cta = t('cta', { returnObjects: true }) as any;

  return (
    <div>
      <SEOHead
        title={t('title')}
        description={t('subtitle')}
        path="/confidentialite"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-aspexit-gray">
            <Link to={`/${currentLocale}`} className="hover:text-aspexit-green">
              {t('breadcrumb.home')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-aspexit-black">
              {t('breadcrumb.current')}
            </span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-10 h-10 text-aspexit-green" />
              <h1 className="text-4xl font-heading font-bold text-aspexit-black">
                {t('title')}
              </h1>
            </div>
            <p className="text-aspexit-gray">
              {t('subtitle')}
            </p>
          </div>

          {/* Alert notice */}
          <div className="bg-blue-50 border-l-4 border-aspexit-purple rounded-lg p-6 mb-12">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-aspexit-purple flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-aspexit-black mb-2">
                  {alert.title}
                </h3>
                <p className="text-sm text-aspexit-gray">
                  {alert.content}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none space-y-12">
            {sections.map((section: any) => (
              <section key={section.id}>
                <h2 className="text-2xl font-heading font-extrabold text-aspexit-black mb-6">
                  {section.number ? `${section.number}. ${section.title}` : section.title}
                </h2>

                {/* Intro for section responsable */}
                {section.intro && section.id === 'responsable' && (
                  <>
                    <p className="text-aspexit-gray mb-4">{section.intro}</p>
                    <div className="bg-aspexit-gray-light rounded-lg p-4 mb-4">
                      <p className="text-aspexit-gray mb-1"><strong>{companyInfo.name}</strong></p>
                      <p className="text-aspexit-gray text-sm">{companyInfo.legalForm}</p>
                      <p className="text-aspexit-gray text-sm">SIREN : {companyInfo.siren}</p>
                      <p className="text-aspexit-gray text-sm">{companyInfo.address}</p>
                      <p className="text-aspexit-gray text-sm">{companyInfo.representative}</p>
                    </div>
                  </>
                )}

                {/* Regular intro */}
                {section.intro && section.id !== 'responsable' && (
                  <p className="text-aspexit-gray mb-4">{section.intro}</p>
                )}

                {/* Regular content */}
                {section.content && section.content.map((para: string, idx: number) => (
                  <p key={idx} className="text-aspexit-gray mb-4">{para}</p>
                ))}

                {/* Subsections (for complex sections like cookies, données) */}
                {section.subsections && section.subsections.map((subsection: any, idx: number) => (
                  <div key={idx} className="mb-6">
                    <h3 className="text-xl font-semibold text-aspexit-black mb-3">
                      {subsection.title}
                    </h3>

                    {subsection.intro && (
                      <p className="text-aspexit-gray mb-4">{subsection.intro}</p>
                    )}

                    {subsection.content && subsection.content.map((para: string, i: number) => (
                      <p key={i} className="text-aspexit-gray mb-4">{para}</p>
                    ))}

                    {/* Categories (for données collectées) */}
                    {subsection.categories && subsection.categories.map((category: any, i: number) => (
                      <div key={i} className="bg-aspexit-gray-light rounded-lg p-6 mb-4">
                        <h4 className="font-semibold text-aspexit-black mb-3">{category.title}</h4>
                        <ul className="list-disc pl-6 text-aspexit-gray space-y-2">
                          {category.items.map((item: string, j: number) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    {/* Simple items list */}
                    {subsection.items && (
                      <ul className="list-disc pl-6 text-aspexit-gray space-y-2">
                        {subsection.items.map((item: string, i: number) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    )}

                    {/* Cookie categories */}
                    {subsection.categories && subsection.categories[0]?.type && subsection.categories.map((category: any, i: number) => (
                      <div key={i} className={`border-l-4 ${category.type === 'necessary' ? 'border-aspexit-green' : 'border-blue-400'} pl-4 mb-6`}>
                        <h4 className="font-semibold text-aspexit-black mb-2">{category.title}</h4>
                        <p className="text-aspexit-gray text-sm mb-3">{category.description}</p>
                        {category.service && (
                          <p className="text-aspexit-gray text-sm mb-2"><strong>{category.service}</strong></p>
                        )}
                        {category.items && (
                          <ul className="list-disc pl-6 text-aspexit-gray text-sm space-y-1">
                            {category.items.map((item: string, j: number) => (
                              <li key={j}>{item}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-aspexit-gray text-sm mt-2">
                          <strong>Durée :</strong> {category.duration}
                        </p>
                      </div>
                    ))}

                    {/* Methods list */}
                    {subsection.methods && (
                      <ul className="list-disc pl-6 text-aspexit-gray space-y-2 mb-4">
                        {subsection.methods.map((method: string, i: number) => (
                          <li key={i}>{method}</li>
                        ))}
                      </ul>
                    )}

                    {/* Warning */}
                    {subsection.warning && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
                        <p className="text-aspexit-gray text-sm">{subsection.warning}</p>
                      </div>
                    )}
                  </div>
                ))}

                {/* Purposes (finalités) */}
                {section.purposes && section.purposes.map((purpose: any, idx: number) => (
                  <div key={idx} className="border-l-4 border-aspexit-green pl-4 mb-4">
                    <h4 className="font-semibold text-aspexit-black mb-2">{purpose.title}</h4>
                    <p className="text-aspexit-gray text-sm">{purpose.legalBasis}</p>
                  </div>
                ))}

                {/* Conservation items */}
                {section.items && section.items[0]?.label && section.items.map((item: any, idx: number) => (
                  <p key={idx} className="text-aspexit-gray mb-2">
                    <strong>{item.label}</strong> {item.value}
                  </p>
                ))}

                {/* Recipients */}
                {section.recipients && section.recipients.map((recipient: any, idx: number) => (
                  <div key={idx} className="bg-aspexit-gray-light rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-aspexit-black mb-2">{recipient.title}</h4>
                    {recipient.description && (
                      <p className="text-aspexit-gray text-sm">{recipient.description}</p>
                    )}
                    {recipient.services && recipient.services.map((service: string, i: number) => (
                      <p key={i} className="text-aspexit-gray text-sm mb-2">{service}</p>
                    ))}
                  </div>
                ))}

                {/* Commitment */}
                {section.commitment && (
                  <div className="bg-green-50 border-l-4 border-aspexit-green rounded-lg p-4 mt-6">
                    <p className="text-aspexit-gray">{section.commitment}</p>
                  </div>
                )}

                {/* Transfers */}
                {section.transfers && section.transfers.map((transfer: any, idx: number) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-aspexit-black mb-2">
                      {transfer.country} {transfer.title}
                    </h4>
                    <p className="text-aspexit-gray text-sm mb-1">
                      <strong>Service :</strong> {transfer.service}
                    </p>
                    <p className="text-aspexit-gray text-sm">
                      <strong>Garanties :</strong> {transfer.guarantee}
                    </p>
                  </div>
                ))}

                {/* Compliance */}
                {section.compliance && (
                  <div className="bg-blue-50 border-l-4 border-aspexit-purple rounded-lg p-4 mt-6">
                    <p className="text-aspexit-gray text-sm">{section.compliance}</p>
                  </div>
                )}

                {/* Protections/Measures lists */}
                {section.protections && (
                  <>
                    <ul className="list-disc pl-6 text-aspexit-gray space-y-2 mb-4">
                      {section.protections.map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                    {section.measuresIntro && (
                      <>
                        <p className="text-aspexit-gray mb-4">{section.measuresIntro}</p>
                        <ul className="list-disc pl-6 text-aspexit-gray space-y-2">
                          {section.measures.map((item: string, idx: number) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                )}

                {/* Rights */}
                {section.rights && (
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {section.rights.map((right: any, idx: number) => (
                      <div key={idx} className="bg-aspexit-gray-light rounded-lg p-4">
                        <h4 className="font-semibold text-aspexit-black mb-2">{right.title}</h4>
                        <p className="text-aspexit-gray text-sm">{right.description}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* How to exercise rights */}
                {section.howToExercise && (
                  <>
                    <h3 className="text-xl font-semibold text-aspexit-black mb-4">
                      {section.howToExercise.title}
                    </h3>
                    <p className="text-aspexit-gray mb-4">{section.howToExercise.intro}</p>
                    <ul className="list-disc pl-6 text-aspexit-gray space-y-2 mb-4">
                      {section.howToExercise.methods.map((method: string, idx: number) => (
                        <li key={idx}>{method}</li>
                      ))}
                    </ul>
                    <p className="text-aspexit-gray mb-4">{section.howToExercise.responseTime}</p>
                    <p className="text-aspexit-gray">
                      {section.howToExercise.cnilText}{' '}
                      <a
                        href={section.howToExercise.cnilUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-aspexit-green hover:underline"
                      >
                        {section.howToExercise.cnilLabel}
                      </a>
                    </p>
                  </>
                )}

                {/* Contact methods */}
                {section.methods && section.methods[0]?.label && (
                  <div className="bg-aspexit-gray-light rounded-lg p-6">
                    {section.methods.map((method: any, idx: number) => (
                      <p key={idx} className="text-aspexit-gray mb-2">
                        <strong>{method.label}</strong> {method.value}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* Date */}
            <section>
              <div className="bg-aspexit-gray-light rounded-lg p-6 text-center">
                <p className="text-sm text-aspexit-gray">
                  <strong>Dernière mise à jour :</strong> {t('lastUpdate')}
                </p>
              </div>
            </section>
          </div>

          {/* CTA Contact */}
          <div className="mt-12 text-center bg-aspexit-green/10 rounded-lg p-8">
            <h3 className="text-xl font-heading font-extrabold text-aspexit-black mb-6">
              {cta.title}
            </h3>
            <p className="text-aspexit-gray mb-6">
              {cta.subtitle}
            </p>
            <Link
              to={`/${currentLocale}/contact`}
              className="inline-block px-6 py-3 bg-aspexit-green text-white rounded-lg font-semibold hover:bg-aspexit-green-dark transition-colors"
            >
              {cta.buttonText}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
